import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators, NonNullableFormBuilder, AbstractControl, ValidatorFn } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, take, filter } from 'rxjs/operators';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  BaseButtonSize,
  BaseDateService,
  NewInquiryFormInterface,
  ShiftTimeResponse,
  WeekdaysArray,
  BaseWeekDays,
  ShiftPlanCalendarDateInterface,
  BaseInquiryShiftsWithFlex,
  getWeekDayFromDate,
  baseInquiryActions,
  BaseCoreState,
  baseInquirySelectors,
  ShiftPlanCalendarRangeInterface,
  atLeastOneWeekday,
  flexShiftsAmountValidator,
  atLeastOnePerson,
  FormRawValue,
  weekdayDateValidator,
  getDaysArrayWithWeekdays,
  BaseShiftIcons,
  BaseInquiryShiftsWithFlexType,
  toNgbDateStruct,
  DEFAULT_MIN_DATE,
  DEFAULT_MAX_DATE,
} from 'base';

@Component({
  selector: 'base-customer-shift-details-modal',
  templateUrl: './base-customer-shift-details-modal.component.html',
})
export class BaseCustomerShiftDetailsModalComponent implements OnInit, OnDestroy {
  @Input() modalRef: NgbModalRef;
  @Input() timeShifts: Array<ShiftTimeResponse>;
  @Input() data: ShiftPlanCalendarDateInterface;
  @Input() currentShift: BaseInquiryShiftsWithFlexType;

  form: FormGroup<NewInquiryFormInterface>;
  buttonSize = BaseButtonSize;
  lastWeekStart = BaseDateService.getStartDayOfLastWeek();
  minDate: NgbDateStruct = DEFAULT_MIN_DATE;
  maxDate: NgbDateStruct = DEFAULT_MAX_DATE;
  submitted = false;
  requirementsOpen = false;
  weekdaysArray = WeekdaysArray;
  shiftsIcons = BaseShiftIcons;
  formData: FormRawValue<FormGroup<NewInquiryFormInterface>>;
  currentDataRange: ShiftPlanCalendarRangeInterface | null;
  inquiryRangeList: ShiftPlanCalendarRangeInterface[] = [];

  destroy$ = new Subject<boolean>();

  constructor(private fb: NonNullableFormBuilder, private store: Store<BaseCoreState>) {}

  ngOnInit() {
    this.minDate = toNgbDateStruct(this.lastWeekStart);

    this.store
      .select(baseInquirySelectors.getShiftPlanRangeList)
      .pipe(
        filter((data) => !!data),
        take(1)
      )
      .subscribe((inquiryRangeList: ShiftPlanCalendarRangeInterface[]) => {
        this.inquiryRangeList = inquiryRangeList;

        this.handleFormData(inquiryRangeList);

        const rangesList: {
          startDate: string;
          endDate: string;
        }[] = inquiryRangeList.map(({ rangeData }) => ({
          startDate: rangeData.startDate,
          endDate: rangeData.endDate,
        }));

        this.setMinAndMaxDates(rangesList);
        this.onDateRangeChanged();
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  handleFormData(inquiryRangeList: ShiftPlanCalendarRangeInterface[]) {
    if (this.data.currentPosition) {
      const currentPosition = this.data.currentPosition;
      this.currentDataRange =
        inquiryRangeList.find((inquiryRange) => inquiryRange.id === currentPosition.rangeId) || null;

      this.formData = this.collectFormData(this.currentDataRange);
    } else {
      this.formData = this.collectFormData(null);
    }
    this.initShiftDetailsForm(this.formData);
  }

  addShiftPlanRange() {
    this.setFormValidators();
    this.submitted = true;

    if (this.form.invalid) {
      return;
    }

    this.submitted = false;

    this.store.dispatch(
      baseInquiryActions.shiftPlanUpdateData({
        payload: {
          data: this.form.getRawValue(),
          rangeId: this.currentDataRange?.id,
        },
      })
    );

    this.modalRef.close(this.form.getRawValue().dates.range.from);
  }

  removeShiftPlanRange() {
    if (!this.currentDataRange) {
      return;
    }

    this.store.dispatch(
      baseInquiryActions.removeShiftPlanRange({
        payload: this.currentDataRange.id,
      })
    );

    const date = this.getPreviousRangeDate(this.currentDataRange!.id);
    this.modalRef.close(date);
  }

  getPreviousRangeDate(rangeId: string): string | null {
    const index = this.inquiryRangeList.findIndex((range) => range.id === rangeId);
    const previousRange = index > 0 ? this.inquiryRangeList[index - 1] : this.inquiryRangeList[0];

    return previousRange?.dates[0]?.dateString || null;
  }

  setFormValidators() {
    const { controls } = this.form;
    const shiftsAndPersons = controls.shiftsAndPersons.controls;
    const dates = controls.dates.controls;

    const updateValidators = (control: AbstractControl, validators: ValidatorFn | ValidatorFn[] | null): void => {
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
    };

    updateValidators(shiftsAndPersons.amountPersons, this.form.value.flexShift ? Validators.min(1) : null);
    updateValidators(shiftsAndPersons.shifts, this.form.value.flexShift ? flexShiftsAmountValidator() : null);
    updateValidators(shiftsAndPersons.persons, this.form.value.flexShift ? null : atLeastOnePerson());
    updateValidators(dates.weekdays, atLeastOneWeekday());
    updateValidators(controls.dates, weekdayDateValidator());

    this.form.markAllAsTouched();
  }

  onDateRangeChanged(): void {
    this.form.controls.dates.controls.range.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((rangeValues) => {
      if (rangeValues.from && rangeValues.to) {
        const isFullWeekOrMoreSelected =
          BaseDateService.differenceInDays(new Date(rangeValues.from), new Date(rangeValues.to)) >= 6;
        const selectedDays = getDaysArrayWithWeekdays<string>(new Date(rangeValues.from), new Date(rangeValues.to)).map(
          (date) => (new Date(date).getDay() + 6) % 7
        );
        WeekdaysArray.forEach((item, index) => {
          this.form.controls.dates.controls.weekdays.controls[item as BaseWeekDays].setValue(
            isFullWeekOrMoreSelected ? true : selectedDays.some((item) => item === index)
          );
        });
      }
    });
  }

  trackByShift(_: number, item: ShiftTimeResponse) {
    return item.type;
  }

  private initShiftDetailsForm(formData: FormRawValue<FormGroup<NewInquiryFormInterface>>) {
    this.form = this.fb.group({
      dates: this.fb.group({
        range: [
          {
            from: formData.dates.range.from,
            to: formData.dates.range.to,
          },
          Validators.required,
        ],
        weekdays: this.fb.group({
          [BaseWeekDays.MO]: [formData.dates.weekdays.mo],
          [BaseWeekDays.TU]: [formData.dates.weekdays.tu],
          [BaseWeekDays.WE]: [formData.dates.weekdays.we],
          [BaseWeekDays.TH]: [formData.dates.weekdays.th],
          [BaseWeekDays.FR]: [formData.dates.weekdays.fr],
          [BaseWeekDays.SA]: [formData.dates.weekdays.sa],
          [BaseWeekDays.SU]: [formData.dates.weekdays.su],
        }),
      }),
      flexShift: [formData.flexShift],
      shiftsAndPersons: this.fb.group({
        shifts: this.fb.group({
          fd: [formData.shiftsAndPersons.shifts.fd],
          zd: [formData.shiftsAndPersons.shifts.zd],
          sd: [formData.shiftsAndPersons.shifts.sd],
          nd: [formData.shiftsAndPersons.shifts.nd],
        }),
        persons: this.fb.group({
          fdPersons: [formData.shiftsAndPersons.persons.fdPersons],
          zdPersons: [formData.shiftsAndPersons.persons.zdPersons],
          sdPersons: [formData.shiftsAndPersons.persons.sdPersons],
          ndPersons: [formData.shiftsAndPersons.persons.ndPersons],
        }),
        amountPersons: [formData.shiftsAndPersons.amountPersons || 1],
      }),
    });
  }

  private collectFormData(rangeItem: ShiftPlanCalendarRangeInterface | null) {
    let formData: FormRawValue<FormGroup<NewInquiryFormInterface>>;

    if (rangeItem) {
      const { rangeData } = rangeItem;

      formData = {
        dates: {
          range: {
            from: rangeData.startDate,
            to: rangeData.endDate,
          },
          weekdays: rangeData.weekdays,
        },
        flexShift: rangeData.flexShift,
        shiftsAndPersons: {
          shifts: {
            fd: rangeData.shifts.fd > 0,
            zd: rangeData.shifts.zd > 0,
            sd: rangeData.shifts.sd > 0,
            nd: rangeData.shifts.nd > 0,
            [this.currentShift]: true,
          },
          persons: {
            fdPersons: rangeData.shifts.fd || +(this.currentShift === BaseInquiryShiftsWithFlex.fd),
            zdPersons: rangeData.shifts.zd || +(this.currentShift === BaseInquiryShiftsWithFlex.zd),
            sdPersons: rangeData.shifts.sd || +(this.currentShift === BaseInquiryShiftsWithFlex.sd),
            ndPersons: rangeData.shifts.nd || +(this.currentShift === BaseInquiryShiftsWithFlex.nd),
          },
          amountPersons: rangeData.amountOfPersons || 1,
        },
      };
    } else {
      const weekDay: BaseWeekDays = getWeekDayFromDate(this.data.dateItem.dateString);

      formData = {
        dates: {
          range: {
            from: this.data.dateItem.dateString,
            to: this.data.dateItem.dateString,
          },
          weekdays: {
            [BaseWeekDays.MO]: weekDay === BaseWeekDays.MO,
            [BaseWeekDays.TU]: weekDay === BaseWeekDays.TU,
            [BaseWeekDays.WE]: weekDay === BaseWeekDays.WE,
            [BaseWeekDays.TH]: weekDay === BaseWeekDays.TH,
            [BaseWeekDays.FR]: weekDay === BaseWeekDays.FR,
            [BaseWeekDays.SA]: weekDay === BaseWeekDays.SA,
            [BaseWeekDays.SU]: weekDay === BaseWeekDays.SU,
          },
        },
        flexShift: this.currentShift === BaseInquiryShiftsWithFlex.flex,
        shiftsAndPersons: {
          shifts: {
            fd:
              this.currentShift === BaseInquiryShiftsWithFlex.flex ||
              this.currentShift === BaseInquiryShiftsWithFlex.fd,
            zd:
              this.currentShift === BaseInquiryShiftsWithFlex.flex ||
              this.currentShift === BaseInquiryShiftsWithFlex.zd,
            sd:
              this.currentShift === BaseInquiryShiftsWithFlex.flex ||
              this.currentShift === BaseInquiryShiftsWithFlex.sd,
            nd:
              this.currentShift === BaseInquiryShiftsWithFlex.flex ||
              this.currentShift === BaseInquiryShiftsWithFlex.nd,
          },
          persons: {
            fdPersons: +(this.currentShift === BaseInquiryShiftsWithFlex.fd),
            zdPersons: +(this.currentShift === BaseInquiryShiftsWithFlex.zd),
            sdPersons: +(this.currentShift === BaseInquiryShiftsWithFlex.sd),
            ndPersons: +(this.currentShift === BaseInquiryShiftsWithFlex.nd),
          },
          amountPersons: 1,
        },
      };
    }

    return formData;
  }

  private setMinAndMaxDates(
    rangeList: {
      startDate: string;
      endDate: string;
    }[]
  ): void {
    const sortedSelectedDates = rangeList.sort((date1, date2) => {
      if (BaseDateService.isBefore(new Date(date1.startDate), new Date(date2.startDate))) {
        return -1;
      }
      if (BaseDateService.isAfter(new Date(date1.startDate), new Date(date2.startDate))) {
        return 1;
      }
      return 0;
    });
    const datesBeforeSelected = sortedSelectedDates.filter((period) => {
      return !!period.endDate
        ? BaseDateService.isBefore(new Date(period.endDate), new Date(this.data.dateItem.dateString))
        : BaseDateService.isBefore(new Date(period.startDate), new Date(this.data.dateItem.dateString));
    });
    const datesAfterSelected = sortedSelectedDates.filter((period) => {
      return BaseDateService.isAfter(new Date(period.startDate), new Date(this.data.dateItem.dateString));
    });

    this.minDate = datesBeforeSelected.length
      ? toNgbDateStruct(
          BaseDateService.getNextDay(
            new Date(
              datesBeforeSelected[datesBeforeSelected.length - 1].endDate ??
                datesBeforeSelected[datesBeforeSelected.length - 1].startDate
            )
          )
        )
      : toNgbDateStruct(this.lastWeekStart);

    this.maxDate = datesAfterSelected.length
      ? toNgbDateStruct(BaseDateService.getPreviousDay(new Date(datesAfterSelected[0].startDate)))
      : DEFAULT_MAX_DATE;
  }
}
